import { Button, Form, Input, Select } from 'antd';
import { type ReactNode, useMemo } from 'react';
import { useQuery } from 'urql';
import { MemberChoicesDocument } from './meters-data';

export type MeterFormValues = {
  memberId: string;
  meterNo: string;
  meterAddress: string;
};

export function MeterForm({
  initialValues,
  saving,
  submitLabel,
  onSubmit,
  onCancel,
  beforeSubmit,
  submitDisabled = false,
}: {
  initialValues: Partial<MeterFormValues>;
  saving: boolean;
  submitLabel: string;
  onSubmit: (values: MeterFormValues) => void;
  onCancel?: () => void;
  /** Shown above the save button (e.g. the new meter's photo). */
  beforeSubmit?: ReactNode;
  /** The save button waits (e.g. while a photo is sent). */
  submitDisabled?: boolean;
}) {
  const [form] = Form.useForm<MeterFormValues>();
  const [{ data }] = useQuery({ query: MemberChoicesDocument });
  const options = useMemo(
    () =>
      (data?.members?.nodes ?? []).flatMap((m) =>
        m
          ? [
              {
                value: m.id,
                label: m.fullName ?? '',
                search: `${m.fullName} ${m.memberAddress}`,
              },
            ]
          : [],
      ),
    [data],
  );

  return (
    <Form
      form={form}
      layout="vertical"
      size="large"
      initialValues={initialValues}
      onFinish={onSubmit}
      className="card member-form"
      requiredMark={false}
    >
      <Form.Item
        name="memberId"
        label="العضو"
        rules={[{ required: true, message: 'اختر العضو' }]}
      >
        <Select
          showSearch
          placeholder="ابحث بالاسم"
          options={options}
          loading={!data}
          optionFilterProp="search"
        />
      </Form.Item>
      <Form.Item
        name="meterNo"
        label="رقم العداد (إن وُجد)"
        extra="اتركه فارغاً إن لم يكن للعداد رقم: يُعرف حينها بعنوانه."
      >
        <Input className="ltr-input" autoComplete="off" />
      </Form.Item>
      <Form.Item
        name="meterAddress"
        label="العنوان أو الاسم المعروف به"
        rules={[{ required: true, whitespace: true, message: 'العنوان مطلوب' }]}
        extra="مثلاً «محمد كرومي _ 12» أو «درب المسجد»."
      >
        <Input autoComplete="off" />
      </Form.Item>
      {beforeSubmit}
      <Button
        type="primary"
        htmlType="submit"
        block
        className="big-button"
        loading={saving}
        disabled={submitDisabled}
      >
        {submitLabel}
      </Button>
      {onCancel && (
        <Button block size="large" onClick={onCancel} className="cancel-button">
          إلغاء
        </Button>
      )}
    </Form>
  );
}
